// App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

const PHI = 0.61803398875;
const FRAME_ASPECT = 1 / PHI; // ≈ 1.618

// Web-standard-ish breakpoints
const BP_SM = 640;  // phones
const BP_LG = 1024; // laptops+

// Landing geometry (normalized 0..1)
const LANDING_DIAG_N = { x1: 0.0, y1: 0.78, x2: 1.0, y2: 0.22 };
const LANDING_LEFT_RAIL_N = { x1: 0.1, y1: 0.0, x2: 0.25, y2: 1.0 };
const LANDING_RIGHT_RAIL_N = { x1: 0.9, y1: 0.0, x2: 0.7, y2: 1.0 };

// Invert everything BELOW the diagonal (on a black base)
const INVERT_CLIP_PATH = `polygon(0% 100%, 100% 100%, ${LANDING_DIAG_N.x2 * 100}% ${LANDING_DIAG_N.y2 * 100
  }%, ${LANDING_DIAG_N.x1 * 100}% ${LANDING_DIAG_N.y1 * 100}%)`;

// tweakable quality
const HOME_LINE_COUNT = 500; // reduce to 160 if you want even faster

// ------------------------------------
// Helpers
// ------------------------------------
function angleDegN(L, w, h) {
  const a = (Math.atan2((L.y2 - L.y1) * h, (L.x2 - L.x1) * w) * 180) / Math.PI;
  if (a > 90) return a - 180;
  if (a < -90) return a + 180;
  return a;
}

function extendSegmentToInfinityPx(x1, y1, x2, y2, extent = 20000) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.max(1e-6, Math.hypot(dx, dy));
  const ux = dx / len;
  const uy = dy / len;
  return {
    x1: x1 - ux * extent,
    y1: y1 - uy * extent,
    x2: x2 + ux * extent,
    y2: y2 + uy * extent,
  };
}

// ------------------------------------
// Precompute the bundle so per-frame work is lighter
// ------------------------------------
function initHomeBundle(w, h, N) {
  const bx = new Float32Array(N);
  const by = new Float32Array(N);
  const phi = new Float32Array(N);
  const envelope = new Float32Array(N);

  const TAU = Math.PI * 2;
  for (let i = 0; i < N; i++) {
    const u = (i + 1) / (N + 1);

    bx[i] =
      (LANDING_DIAG_N.x1 + (LANDING_DIAG_N.x2 - LANDING_DIAG_N.x1) * u) * w;
    by[i] =
      (LANDING_DIAG_N.y1 + (LANDING_DIAG_N.y2 - LANDING_DIAG_N.y1) * u) * h;

    const ph = (i / Math.max(1, N - 1)) * TAU;
    phi[i] = ph;
    envelope[i] = 0.62 + 0.38 * Math.sin(ph * 0.5);
  }

  // tangent + normal
  const dx = (LANDING_DIAG_N.x2 - LANDING_DIAG_N.x1) * w;
  const dy = (LANDING_DIAG_N.y2 - LANDING_DIAG_N.y1) * h;
  const tanLen = Math.max(1e-6, Math.hypot(dx, dy));
  const tx = dx / tanLen;
  const ty = dy / tanLen;

  let nx = -ty;
  let ny = tx;
  if (ny < 0) {
    nx *= -1;
    ny *= -1;
  }

  return { w, h, N, bx, by, phi, envelope, tx, ty, nx, ny };
}

// ------------------------------------
// Build HOME lines (travelling wave + local mouse warp)
// ------------------------------------
function buildHomeLines(bundle, t, mouse) {
  const { w, h, N, bx, by, phi, envelope, tx, ty, nx, ny } = bundle;

  const lines = [];

  // rails
  lines.push(
    extendSegmentToInfinityPx(
      LANDING_LEFT_RAIL_N.x1 * w,
      LANDING_LEFT_RAIL_N.y1 * h,
      LANDING_LEFT_RAIL_N.x2 * w,
      LANDING_LEFT_RAIL_N.y2 * h
    )
  );
  lines.push(
    extendSegmentToInfinityPx(
      LANDING_RIGHT_RAIL_N.x1 * w,
      LANDING_RIGHT_RAIL_N.y1 * h,
      LANDING_RIGHT_RAIL_N.x2 * w,
      LANDING_RIGHT_RAIL_N.y2 * h
    )
  );

  // wave tuning
  const speed = 0.85;
  const cycles = 1.25;

  // mouse influence
  const mx = mouse.x ?? w * 0.5;
  const my = mouse.y ?? h * 0.5;
  const hasMouse = mouse.has;
  const hold = mouse.down ? 1 : 0;
  const strength = (hasMouse ? 0.35 : 0.0) + (hasMouse ? hold * 1.0 : 0.0);

  const sigma = Math.min(w, h) * 0.18;
  const invTwoSigma2 = 1 / (2 * sigma * sigma);

  for (let i = 0; i < N; i++) {
    const baseX = bx[i];
    const baseY = by[i];
    const ph = phi[i];
    const env = envelope[i];

    let p = t * speed - ph * cycles;

    let anchorDrift = 0;
    if (strength > 0) {
      const ddx = mx - baseX;
      const ddy = my - baseY;
      const d2 = ddx * ddx + ddy * ddy;
      const influence = Math.exp(-d2 * invTwoSigma2);

      p += strength * influence * 2.2;
      anchorDrift = strength * influence * (10 + 10 * Math.sin(ph * 0.5));
    }

    const anchorAlong = (10 + 10 * env) * Math.sin(p);
    const ax = baseX + tx * (anchorAlong * 0.22 + anchorDrift * 0.12);
    const ay = baseY + ty * (anchorAlong * 0.22 + anchorDrift * 0.12);

    const normalAmp = 0.28 * h * env;
    const normalDisp = normalAmp * Math.sin(p + 0.2);

    const tangAmp = 0.06 * h * env;
    const tangDisp = tangAmp * Math.sin(p * 1.1 + 0.8);

    const yAmp = 0.045 * h * env;
    const yDisp = yAmp * Math.sin(p * 1.25 + 1.6);

    const ex = ax + nx * normalDisp + tx * tangDisp;
    const ey = ay + ny * normalDisp + ty * tangDisp + yDisp;

    lines.push({ x1: ax, y1: ay, x2: ex, y2: ey });
  }

  return lines;
}

// ------------------------------------
// Imperatively update SVG lines (no React state per frame)
// ------------------------------------
function ensureSvgLines(g, count) {
  while (g.childNodes.length < count) {
    g.appendChild(document.createElementNS("http://www.w3.org/2000/svg", "line"));
  }
  while (g.childNodes.length > count) {
    g.removeChild(g.lastChild);
  }
}

function updateSvgLines(g, lines) {
  ensureSvgLines(g, lines.length);
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    const el = g.childNodes[i];
    el.setAttribute("x1", ln.x1);
    el.setAttribute("y1", ln.y1);
    el.setAttribute("x2", ln.x2);
    el.setAttribute("y2", ln.y2);
    el.setAttribute("vector-effect", "non-scaling-stroke");
  }
}

// ------------------------------------
// App
// ------------------------------------
export default function App() {
  const [currentPage, setCurrentPage] = useState(0);

  // frame sizing
  const frameOuterRef = useRef(null);
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = frameOuterRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      const availW = Math.round(rect.width);
      const availH = Math.round(rect.height);

      let w = availW;
      let h = Math.round(w / FRAME_ASPECT);

      if (h > availH) {
        h = availH;
        w = Math.round(h * FRAME_ASPECT);
      }

      setFrameSize({ w: Math.max(1, w), h: Math.max(1, h) });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // “web standard” responsive helpers
  const isMobile = frameSize.w < BP_SM;
  const isLandscape = frameSize.w > frameSize.h;

  // If horizontal space is tight, stack (use vertical space) instead of squeezing.
  // This matches your “don’t collapse into the middle” request.
  const stackProjects = frameSize.w < BP_LG;

  // mouse (HOME only)
  const mouseRef = useRef({ x: 0, y: 0, down: false, has: false });
  const onPointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseRef.current.x = e.clientX - rect.left;
    mouseRef.current.y = e.clientY - rect.top;
    mouseRef.current.has = true;
  };
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    mouseRef.current.down = true;
    mouseRef.current.has = true;
  };
  const onPointerUp = () => {
    mouseRef.current.down = false;
  };
  const onPointerLeave = () => {
    mouseRef.current.down = false;
    mouseRef.current.has = false;
  };

  // bundle precompute + SVG refs
  const bundleRef = useRef(null);
  const lineGroupRef = useRef(null);

  useEffect(() => {
    if (!frameSize.w || !frameSize.h) return;
    bundleRef.current = initHomeBundle(frameSize.w, frameSize.h, HOME_LINE_COUNT);
  }, [frameSize.w, frameSize.h]);

  useEffect(() => {
    if (currentPage !== 0) return;
    if (!frameSize.w || !frameSize.h) return;

    let raf = 0;
    const start = performance.now();

    const tick = (now) => {
      const g = lineGroupRef.current;
      const bundle = bundleRef.current;
      if (g && bundle) {
        const t = (now - start) / 1000;
        const lines = buildHomeLines(bundle, t, mouseRef.current);
        updateSvgLines(g, lines);
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentPage, frameSize.w, frameSize.h]);

  // projects
  const projects = useMemo(
    () => [
      { title: "Real-time visuals", image: "/projects/project-one.jpg" },
      { title: "Audio software", image: "/projects/project-two.jpg" },
      { title: "Generative systems", image: "/projects/project-three.jpg" },
      { title: "3D + pipelines", image: "/projects/project-four.jpg" },
      { title: "Automation + tooling", image: "/projects/project-five.jpg" },
      { title: "Production + engineering", image: "/projects/project-six.jpg" },
    ],
    []
  );

  // ------------------------------------
  // HOME texture layer
  // ------------------------------------
  const renderHomeTexture = () => {
    const imgs = [projects[0]?.image, projects[2]?.image, projects[4]?.image].filter(Boolean);

    return (
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          overflow: "hidden",
          background:
            "radial-gradient(120% 90% at 30% 20%, rgba(255,255,255,0.06), rgba(0,0,0,0) 55%), radial-gradient(100% 80% at 80% 70%, rgba(255,255,255,0.04), rgba(0,0,0,0) 60%), #000",
        }}
      >
        {imgs.map((src, i) => {
          const placements = [
            { left: "-12%", top: "-10%", w: "58%", h: "58%", r: -10, delay: "0s" },
            { left: "44%", top: "-8%", w: "64%", h: "64%", r: 8, delay: "-6s" },
            { left: "10%", top: "46%", w: "72%", h: "72%", r: -4, delay: "-12s" },
          ];
          const p = placements[i] || placements[0];

          return (
            <img
              key={i}
              src={src}
              alt=""
              style={{
                position: "absolute",
                left: p.left,
                top: p.top,
                width: p.w,
                height: p.h,
                objectFit: "cover",
                display: "block",
                opacity: 0.12,
                filter: "grayscale(1) contrast(1.05) brightness(0.75) blur(2px)",
                transform: `rotate(${p.r}deg)`,
                mixBlendMode: "soft-light",
                willChange: "transform",
                animation: `pwFloat 22s ease-in-out infinite`,
                animationDelay: p.delay,
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          );
        })}

        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.12,
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, rgba(0,0,0,0) 2px, rgba(0,0,0,0) 4px)",
            mixBlendMode: "overlay",
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(90% 80% at 50% 50%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.35) 85%, rgba(0,0,0,0.65) 110%)",
            pointerEvents: "none",
          }}
        />

        <style>{`
          @keyframes pwFloat {
            0%   { transform: translate3d(0px, 0px, 0px) rotate(0deg); }
            50%  { transform: translate3d(10px, 14px, 0px) rotate(0.6deg); }
            100% { transform: translate3d(0px, 0px, 0px) rotate(0deg); }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes pwFloat { 0%,100% { transform: none; } }
          }
        `}</style>
      </div>
    );
  };

  const renderHomeText = () => {
    const w = frameSize.w || 1;
    const h = frameSize.h || 1;

    const diagAngle = angleDegN(LANDING_DIAG_N, w, h);
    const centerX = w * 0.5;
    const centerY = isMobile ? h * 0.58 : h * 0.62;

    const titleSize = isMobile ? "clamp(54px, 16vw, 140px)" : "clamp(88px, 10vw, 190px)";

    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 6 }}>
        <div
          style={{
            position: "absolute",
            left: `${centerX}px`,
            top: `${centerY}px`,
            transform: `translate(-50%, -50%) rotate(${diagAngle}deg)`,
            transformOrigin: "50% 50%",
            color: "#fff",
            fontSize: titleSize,
            lineHeight: 0.92,
            letterSpacing: "0.02em",
            fontWeight: 800,
            textTransform: "uppercase",
            opacity: 0.98,
            whiteSpace: "nowrap",
            textShadow: "0 2px 18px rgba(0,0,0,0.60)",
          }}
        >
          PHASEWERK
        </div>

        {!isMobile && (
          <div
            style={{
              position: "absolute",
              left: 16,
              bottom: 14,
              fontSize: 12,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            (hover to bend • hold to push)
          </div>
        )}
      </div>
    );
  };

  const renderProjects = () => {
    // Keep content from hiding under the fixed centered footer.
    const footerReserve = isMobile ? 190 : 160;

    // When stacked, keep tiles to 1 column on phones, 2 on tablets.
    const tilesCols = stackProjects
      ? isMobile
        ? "1fr"
        : "repeat(2, minmax(0, 1fr))"
      : "repeat(2, minmax(0, 1fr))";

    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          padding: "clamp(18px, 2.6vw, 34px)",
          display: "grid",
          gap: "clamp(12px, 1.6vw, 18px)",
          zIndex: 6,

          overflowY: "auto",
          overflowX: "hidden",
          alignContent: "start",
          paddingBottom: footerReserve,

          // KEY CHANGE: when narrow, stack into one column so it uses vertical space.
          gridTemplateColumns: stackProjects ? "1fr" : "repeat(12, 1fr)",
        }}
      >
        {/* TEXT */}
        <div
          style={{
            gridColumn: stackProjects ? "1 / -1" : "span 5",
            borderRadius: 14,
            background: "#000",
            border: "1px solid rgba(255,255,255,0.28)",
            boxShadow: "0 18px 48px rgba(0,0,0,0.70)",
            padding: 16,
            alignSelf: "start",
            color: "#fff",
            minWidth: 0,

            // On wide layouts, allow internal scroll; on stacked layouts, let it flow vertically.
            maxHeight: stackProjects ? "none" : "calc(100% - 0px)",
            overflowY: stackProjects ? "visible" : "auto",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.92)",
              marginBottom: 10,
            }}
          >
            Overview
          </div>

          <div style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.88)" }}>
            I build and run audio + visual systems for the creative arts — from real-time visuals and
            generative tools to audio software, show control, and technical delivery.
          </div>

          <div style={{ height: 12 }} />

          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.92)",
              marginBottom: 10,
            }}
          >
            Recent collaborations
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.86)" }}>
            I’ve worked across music, film, and contemporary art with artists, directors, and institutions —
            including Technical Lead for Chris Cunningham in Los Angeles, working with Grace Jones, Robert
            Pattinson, Deep Voodoo (Trey Parker & Matt Stone), Drake, Namethemachine and more. Work has been
            shown at Jeffrey Deitch and other galleries. A full portfolio is available in person, as I
            respect non-disclosure agreements and the artists’ work.
          </div>

          <div style={{ height: 10 }} />

          <div style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.86)" }}>
            In the UK my work has appeared in spaces such as Tate Modern, Bristol Light & Sound Festival,
            State51 and many more. My role typically spans real-time audiovisual systems, show control,
            technical delivery, and custom software/tooling — bridging creative intent with reliable
            engineering on stage, in galleries, and in production environments.
          </div>

          <div style={{ height: 12 }} />

          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.92)",
              marginBottom: 10,
            }}
          >
            Software competency
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.86)" }}>
            Max/MSP + Jitter, TouchDesigner, Unity, Unreal Engine, openFrameworks, Processing,
            WebGL/OpenGL, shader programming, and realtime visual/VFX pipelines. Audio-wise: VST
            development (JUCE), C++ and Python for DSP/tools, plus production workflows in Cubase,
            Ableton Live, Logic Pro, After Effects and DaVinci Resolve. For 3D and content: Blender,
            Houdini, Substance Painter, and 3D modelling. I also build interactive installations and AR
            systems, with hardware/tooling via Node.js, Git, Arduino and Raspberry Pi, alongside
            projection mapping and show-ready realtime delivery.
          </div>

          <div style={{ height: 12 }} />

          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
              marginBottom: 8,
            }}
          >
            What I do
          </div>

          <ul
            style={{
              margin: 0,
              paddingLeft: 16,
              fontSize: 13,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            <li>Real-time visuals (TouchDesigner, show control, mapping)</li>
            <li>Generative systems (Max/MSP + Jitter, shaders, OSC/MIDI)</li>
            <li>Audio software (C++ DSP, real-time pipelines, plugins)</li>
            <li>3D workflows (Blender, WebGL/Three.js, assets/pipelines)</li>
            <li>Automation & tooling (JavaScript, APIs, document generation)</li>
            <li>Production & engineering (live sound, systems, technical support)</li>
          </ul>
        </div>

        {/* TILES */}
        <div
          style={{
            gridColumn: stackProjects ? "1 / -1" : "span 7",
            display: "grid",
            gridTemplateColumns: tilesCols,
            gap: "clamp(12px, 1.6vw, 18px)",
            minWidth: 0,
            alignContent: "start",
          }}
        >
          {projects.map((p, idx) => (
            <div
              key={idx}
              style={{
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.04)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                minHeight: isMobile && isLandscape ? 160 : 210,
              }}
            >
              <div style={{ position: "relative", aspectRatio: "16 / 10" }}>
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    filter: "grayscale(1) contrast(1.12) brightness(0.9)",
                    opacity: 0.98,
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(0,0,0,0.10), rgba(0,0,0,0.62))",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 12,
                    bottom: 10,
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.92)",
                    textShadow: "0 2px 14px rgba(0,0,0,0.75)",
                  }}
                >
                  {p.title}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="App" style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
      <div className="frameOuter" ref={frameOuterRef}>
        <div
          className="frame"
          style={{
            width: `${frameSize.w}px`,
            height: `${frameSize.h}px`,
            background: "#000",
            position: "relative",
            cursor: currentPage === 0 ? "crosshair" : "default",
            isolation: "isolate",
            overflow: "hidden",
          }}
          onPointerMove={currentPage === 0 ? onPointerMove : undefined}
          onPointerDown={currentPage === 0 ? onPointerDown : undefined}
          onPointerUp={currentPage === 0 ? onPointerUp : undefined}
          onPointerLeave={currentPage === 0 ? onPointerLeave : undefined}
        >
          {/* HOME TEXTURE (HOME ONLY) */}
          {currentPage === 0 && renderHomeTexture()}

          {/* LINES */}
          <svg
            className="gridSvg"
            viewBox={`0 0 ${Math.max(frameSize.w, 1)} ${Math.max(frameSize.h, 1)}`}
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, zIndex: 3 }}
          >
            <g ref={lineGroupRef} className="gridStroke" style={{ stroke: "#fff", opacity: 0.92 }} />
          </svg>

          {/* CONTENT */}
          <div style={{ position: "absolute", inset: 0, zIndex: 6 }}>
            {currentPage === 0 ? renderHomeText() : renderProjects()}
          </div>

          {/* INVERT BELOW DIAGONAL (HOME ONLY) */}
          {currentPage === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "#fff",
                mixBlendMode: "difference",
                pointerEvents: "none",
                clipPath: INVERT_CLIP_PATH,
                zIndex: 10,
              }}
            />
          )}
        </div>
      </div>

      {/* FOOTER GUTTER — centered */}
      <div
        className="contactBlock"
        style={{
          color: "#fff",
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "max(12px, env(safe-area-inset-bottom))",
          zIndex: 2000,

          width: isMobile ? "calc(100vw - 24px)" : 360,
          maxWidth: "calc(100vw - 24px)",

          display: "grid",
          gap: 10,
          padding: isMobile ? 10 : 12,
          borderRadius: 14,
          background: "rgba(0,0,0,0.78)",
          border: "1px solid rgba(255,255,255,0.22)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",

          // center everything inside
          justifyItems: "center",
          textAlign: "center",
        }}
      >
        {/* NAV */}
        <div style={{ display: "flex", gap: 10, width: "100%", justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => setCurrentPage(0)}
            aria-pressed={currentPage === 0}
            style={{
              cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.26)",
              background: currentPage === 0 ? "rgba(255,255,255,0.16)" : "transparent",
              color: "#fff",
              padding: isMobile ? "9px 10px" : "10px 12px",
              borderRadius: 12,
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              flex: 1,
            }}
          >
            Home
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            aria-pressed={currentPage === 1}
            style={{
              cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.26)",
              background: currentPage === 1 ? "rgba(255,255,255,0.16)" : "transparent",
              color: "#fff",
              padding: isMobile ? "9px 10px" : "10px 12px",
              borderRadius: 12,
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              flex: 1,
            }}
          >
            Projects
          </button>
        </div>

        {/* CONTACT — centered */}
        <div style={{ display: "grid", gap: 6, justifyItems: "center" }}>
          <a
            href="mailto:tom@phasewerk.com"
            style={{
              color: "#fff",
              textDecoration: "none",
              borderBottom: "1px solid rgba(255,255,255,0.32)",
              paddingBottom: 1,
              fontSize: 12,
              letterSpacing: "0.08em",
              overflowWrap: "anywhere",
            }}
          >
            TOM@PHASEWERK.COM
          </a>

          <a
            href="tel:+447460531289"
            style={{
              color: "#fff",
              textDecoration: "none",
              borderBottom: "1px solid rgba(255,255,255,0.32)",
              paddingBottom: 1,
              fontSize: 10,
              letterSpacing: "0.08em",
              whiteSpace: "nowrap",
            }}
          >

            Designed with React and Three.js

          </a>
        </div>
      </div>
    </div>
  );
} 